<template>
  <section class="destination-grid-section">
    <h3 class="section-title">
      <span class="section-icon">🌍</span>
      Destinations
    </h3>

    <!-- Loading: 6 shimmer tiles reserving grid shape -->
    <div v-if="loading" class="destination-grid" aria-hidden="true">
      <div v-for="n in 6" :key="`dest-skel-${n}`" class="destination-card destination-card--skeleton">
        <div class="dest-skeleton shimmer"></div>
      </div>
    </div>

    <!-- Error / empty: static canonical fallback list (still routes correctly) -->
    <div v-else class="destination-grid">
      <a
        v-for="tile in tiles"
        :key="`dest-${tile.id}`"
        class="destination-card"
        :style="{ background: tile.gradient }"
        :href="`/cruises?destination=${encodeURIComponent(tile.routeId)}`"
        @click.prevent="goToDestination(tile.routeId)"
      >
        <span class="dest-overlay" aria-hidden="true"></span>
        <i class="dest-motive" :class="tile.icon" aria-hidden="true"></i>
        <span class="dest-body">
          <span class="dest-title">{{ tile.title }}</span>
          <span class="dest-sub">
            {{ tile.sailingCount > 0 ? `${formatNumber(tile.sailingCount)} sailings tracked` : 'Explore cruises' }}
          </span>
        </span>
        <span v-if="tile.minPricePerNight != null" class="dest-price-pill">
          From ${{ formatPrice(tile.minPricePerNight) }}/night
        </span>
      </a>
    </div>
  </section>
</template>

<script>
import apiService from '@/services/api'

import { formatPriceAmount } from '@/utils/formatMoney'
// Canonical presentation tiles in the spec's fixed order. Each references one or
// more canonical destination ids; `multi` merges counts/price (Europe + Mediterranean).
// CP-262: Bahamas uses Free FA water (Pro palm was blank; umbrella-beach already used by Caribbean).
// CP-212: `scrim` is an optional uniform black layer under the tile text, for the lighter
// gradients (Bahamas, Mexican Riviera, a little on Caribbean) so white 13px text clears 4.5:1.
// Hue and gradient direction stay the same; the tile only gets a little deeper.
const DESTINATION_TILES = [
  { id: 'caribbean', title: 'Caribbean', gradient: 'linear-gradient(135deg, #0B5B63 0%, #1697A6 100%)', scrim: 0.12, icon: 'fas fa-umbrella-beach' },
  { id: 'alaska', title: 'Alaska', gradient: 'linear-gradient(135deg, #1A3D54 0%, #4A7C9B 100%)', icon: 'fas fa-mountain' },
  { id: 'europe', title: 'Europe & Mediterranean', gradient: 'linear-gradient(135deg, #9E472A 0%, #3A6B88 100%)', icon: 'fas fa-compass', multi: ['europe', 'mediterranean'] },
  { id: 'bahamas', title: 'Bahamas', gradient: 'linear-gradient(135deg, #008B8B 0%, #48D1CC 100%)', scrim: 0.34, icon: 'fas fa-water' },
  { id: 'mexico', title: 'Mexican Riviera', gradient: 'linear-gradient(135deg, #C45C26 0%, #E28743 100%)', scrim: 0.22, icon: 'fas fa-sun' },
  { id: 'bermuda', title: 'Bermuda & Atlantic', gradient: 'linear-gradient(135deg, #083F45 0%, #1B6B76 100%)', icon: 'fas fa-anchor' }
]

function tileBackground(def) {
  const a = Number(def.scrim)
  if (!Number.isFinite(a) || a <= 0) return def.gradient
  return `linear-gradient(rgba(0, 0, 0, ${a}), rgba(0, 0, 0, ${a})), ${def.gradient}`
}

export default {
  name: 'DestinationGrid',
  data() {
    return {
      destinations: [],
      loading: true
    }
  },
  computed: {
    tiles() {
      const apiMap = new Map(this.destinations.map(d => [d.id, d]))
      return DESTINATION_TILES.map(def => {
        const sources = def.multi || [def.id]
        let sailingCount = 0
        let minPricePerNight = null
        for (const sid of sources) {
          const d = apiMap.get(sid)
          if (!d) continue
          sailingCount += d.sailing_count || 0
          const ppn = d.min_price_per_night
          if (ppn != null && (minPricePerNight == null || ppn < minPricePerNight)) {
            minPricePerNight = ppn
          }
        }
        return {
          id: def.id,
          routeId: def.routeId || def.id,
          title: def.title,
          gradient: tileBackground(def),
          icon: def.icon,
          sailingCount,
          minPricePerNight
        }
      })
    }
  },
  mounted() {
    this.fetchDestinations()
  },
  methods: {
    async fetchDestinations() {
      this.loading = true
      try {
        const response = await apiService.getHomeDestinations()
        this.destinations = Array.isArray(response.data) ? response.data : []
      } catch (e) {
        console.error('Error fetching home destinations:', e)
        this.destinations = []
      } finally {
        this.loading = false
      }
    },
    goToDestination(id) {
      this.$router.push({ path: '/cruises', query: { destination: id } })
    },
    formatNumber(value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return '0'
      return n.toLocaleString('en-US')
    },
    formatPrice(price) {
      return formatPriceAmount(price)
    }
  }
}
</script>

<style scoped>
.destination-grid-section {
  padding: var(--space-6) 0;
}

.section-title {
  color: var(--cs-ink);
  margin: 0 0 var(--space-4);
  font-size: 1.25rem;
  font-family: var(--font-heading);
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.section-icon { font-size: 1.4rem; }

.destination-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-4);
}

.destination-card {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-height: 140px;
  aspect-ratio: 16 / 10;
  border-radius: var(--radius-lg);
  overflow: hidden;
  text-decoration: none;
  color: #fff;
  padding: var(--space-4);
  box-sizing: border-box;
  cursor: pointer;
  transition: transform var(--transition-normal), box-shadow var(--transition-normal);
}

.destination-card:hover {
  transform: translateY(-3px);
  box-shadow: var(--shadow-card-hover);
}

.dest-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 30%, rgba(0, 0, 0, 0.45) 100%);
}

.dest-motive {
  position: absolute;
  top: var(--space-3);
  right: var(--space-3);
  font-size: 2.25rem;
  opacity: 0.3;
}

.dest-body {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.dest-title {
  font-family: var(--font-heading);
  font-size: 20px;
  font-weight: 700;
  color: #fff;
  line-height: 1.2;
}

.dest-sub {
  font-size: var(--text-small);
  color: rgba(255, 255, 255, 0.85);
}

.dest-price-pill {
  position: relative;
  z-index: 1;
  align-self: flex-start;
  margin-top: var(--space-2);
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  color: #A7F3D0;
  font-weight: 700;
  font-size: 13px;
  padding: 3px 8px;
  border-radius: var(--radius-pill);
}

/* Skeleton tile filling the same box */
.destination-card--skeleton {
  pointer-events: none;
  background: var(--cs-card) !important;
  border: 1px solid var(--cs-card-border);
  padding: 0;
}

.dest-skeleton {
  width: 100%;
  height: 100%;
  border-radius: var(--radius-lg);
}

.shimmer {
  background: linear-gradient(
    90deg,
    var(--color-border-light) 0%,
    var(--cs-signal-mist) 40%,
    var(--color-border-light) 80%
  );
  background-size: 300% 100%;
  animation: dest-shimmer 1.4s ease-in-out infinite;
}

@keyframes dest-shimmer {
  0% { background-position: 150% 0; }
  100% { background-position: -150% 0; }
}

/* Tablet: 2 columns */
@media (max-width: 1023px) {
  .destination-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* Mobile: 2 columns, compact tiles */
@media (max-width: 768px) {
  .destination-grid {
    gap: var(--space-2);
  }

  .destination-card {
    min-height: 105px;
    aspect-ratio: auto;
    padding: var(--space-3);
  }

  .dest-title {
    font-size: 16px;
  }

  .dest-price-pill {
    font-size: 11px;
  }

  .dest-motive {
    font-size: 1.75rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .shimmer {
    animation: none;
  }
}
</style>